/**
 * Ojar Multi-Currency System (Refactored v2.2)
 * Enhanced with client-side price application from OjarPriceData
 *
 * Handles:
 * 1. Geo-detection
 * 2. Currency selection & switching
 * 3. Cookie/localStorage management for Liquid templates
 * 4. Client-side price formatting from OjarPriceData global object
 * 5. Travel & Gift Box discount display in cart/drawer
 * 6. DEFENSIVE: Self-healing watcher to handle race conditions with other scripts
 *
 * Price rendering approach:
 * - Server renders Shopify prices as fallback
 * - Client applies custom prices from window.OjarPriceData if available
 * - No MutationObservers or DOM polling - only event-based updates
 *
 * Discount priority (matches backend create-checkout-session.js):
 * - Gift Box discount takes priority over Travel discount
 * - Travel discount only shows if NO gift box items in cart
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    storageKey: "ojar_currency",
    countryKey: "ojar_country",
    cookieName: "ojar_currency",
    cookieCountryName: "ojar_country",
    cookieDays: 365,
    geoApiUrl: "https://ipapi.co/json/?key=atGFPRahVoFX7tQ3gMABQVdz7W1Eve1W9xWAWXp0LhFhRO6CQy",
    defaultCurrency: "USD",
    defaultCountry: "US",
  };

  // Supported currencies
  const SUPPORTED_CURRENCIES = [
    "USD",
    "EUR",
    "GBP",
    "SAR",
    "AED",
    "QAR",
    "OMR",
    "KWD",
    "BHD",
  ];

  // Currency formatting configuration (decimals and symbols)
  const CURRENCY_SYMBOLS = {
    USD: { symbol: "$", decimals: 2 },
    EUR: { symbol: "€", decimals: 2 },
    GBP: { symbol: "£", decimals: 2 },
    SAR: { symbol: "SAR ", decimals: 0 },
    AED: { symbol: "AED ", decimals: 0 },
    QAR: { symbol: "QAR ", decimals: 0 },
    OMR: { symbol: "OMR ", decimals: 3 },
    KWD: { symbol: "KWD ", decimals: 3 },
    BHD: { symbol: "BHD ", decimals: 3 },
  };

  // Country to Currency mapping
  const COUNTRY_CURRENCY_MAP = {
    // GCC
    AE: "AED",
    SA: "SAR",
    KW: "KWD",
    BH: "BHD",
    OM: "OMR",
    QA: "QAR",
    // Europe
    GB: "GBP",
    DE: "EUR",
    FR: "EUR",
    IT: "EUR",
    ES: "EUR",
    NL: "EUR",
    BE: "EUR",
    AT: "EUR",
    IE: "EUR",
    PT: "EUR",
    GR: "EUR",
    FI: "EUR",
    LU: "EUR",
    MT: "EUR",
    CY: "EUR",
    SK: "EUR",
    SI: "EUR",
    EE: "EUR",
    LV: "EUR",
    LT: "EUR",
    // North America
    US: "USD",
    CA: "USD",
  };

  // State
  let currentCurrency = CONFIG.defaultCurrency;
  let currentCountry = CONFIG.defaultCountry;

  // ===== ABSOLUTE OILS DISCOUNT (display) — mirrors backend =====
  // Items tagged 'absolute-oil' get: 1 item = 10%, 2 = 25%, 3+ = 40%.
  // Campaign window: 1 Sept 2026 (00:00 UTC) -> 30 Sept 2026 (23:59:59 UTC).
  // Month is 0-indexed: 8 = September.
  const ABSOLUTE_OFFER_START_UTC = Date.UTC(2026, 8, 1, 0, 0, 0);
  const ABSOLUTE_OFFER_END_UTC = Date.UTC(2026, 0, 1, 0, 0, 0);
  let ojTravelMap = {};
  let ojTravelMapBuilding = false;

  function ojGetTravelPct(travelQty) {
    // Absolute Oils campaign window: 1 Sept 2026 -> 30 Sept 2026 (UTC).
    var nowMs = Date.now();
    if (nowMs < ABSOLUTE_OFFER_START_UTC || nowMs > ABSOLUTE_OFFER_END_UTC) return 0;
    if (travelQty >= 3) return 0.40;
    if (travelQty === 2) return 0.25;
    if (travelQty === 1) return 0.10;
    return 0;
  }

  function ojBuildTravelMap() {
    if (ojTravelMapBuilding) return;
    ojTravelMapBuilding = true;
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart.items || []);
        var unknown = items.filter(function (it) {
          return !ojTravelMap.hasOwnProperty(it.handle);
        });
        if (unknown.length === 0) { ojTravelMapBuilding = false; return; }
        return Promise.all(unknown.map(function (it) {
          return fetch("/products/" + it.handle + ".js")
            .then(function (r) { return r.json(); })
            .then(function (prod) {
              var tags = (prod && prod.tags) || [];
              ojTravelMap[it.handle] = tags.some(function (t) {
                return String(t).toLowerCase() === "absolute-oil";
              });
            })
            .catch(function () { ojTravelMap[it.handle] = false; });
        })).then(function () {
          ojTravelMapBuilding = false;
          try { applyMultiCurrencyPrices(); } catch (e) {}
        });
      })
      .catch(function () { ojTravelMapBuilding = false; });
  }

  function ojComputeTravelDiscount(scope) {
    try {
      var root = scope || document;
      var rows = root.querySelectorAll("tr[data-product-handle]");
      var travelQty = 0, travelSubtotal = 0;
      rows.forEach(function (item) {
        var handle = item.dataset.productHandle;
        if (!handle || ojTravelMap[handle] !== true) return;
        var qtyInput =
          item.querySelector("[data-quantity-variant-id]") ||
          item.querySelector("input[type='number'][readonly]") ||
          item.querySelector("input[type='number']");
        var qty = qtyInput ? (parseInt(qtyInput.value) || 0) : 0;
        if (qty <= 0) return;
        var price = (window.OjarPriceData && window.OjarPriceData[handle])
          ? window.OjarPriceData[handle][currentCurrency] : undefined;
        if (price === undefined) return;
        travelQty += qty;
        travelSubtotal += price * qty;
      });
      var pct = ojGetTravelPct(travelQty);
      return { pct: pct, amount: pct > 0 ? travelSubtotal * pct : 0 };
    } catch (e) {
      return { pct: 0, amount: 0 };
    }
  }

  function ojRenderTravelLine(totalEl, info) {
    try {
      var container =
        totalEl.closest("cart-drawer") ||
        totalEl.closest(".drawer__footer") ||
        totalEl.closest("#main-cart-footer") ||
        totalEl.closest(".cart__footer") ||
        totalEl.parentElement;
      if (!container) return;
      var existing = container.querySelector(".oj-travel-discount-line");
      if (existing) existing.parentNode.removeChild(existing);
      if (!info || info.pct <= 0 || info.amount <= 0) return;
      var pctLabel = Math.round(info.pct * 100);
      var line = document.createElement("div");
      line.className = "oj-travel-discount-line";
      line.setAttribute("style",
        "display:flex;justify-content:space-between;align-items:center;" +
        "margin:6px 0;font-size:14px;color:#2e7d32;font-weight:600;");
      line.innerHTML =
        '<span>Absolute Oils - ' + pctLabel + '% Off</span>' +
        '<span>-' + formatPrice(info.amount, currentCurrency) + '</span>';
      var totalRow = totalEl.closest(".totals") || totalEl.parentElement;
      if (totalRow && totalRow.parentNode) {
        totalRow.parentNode.insertBefore(line, totalRow);
      }
    } catch (e) {}
  }

  // ===== GIFT BOX DISCOUNT (display) — mirrors backend =====
  // Gift box items are identified by _gift_box property in cart line item properties
  // Discount is 20% on gift box items' subtotal (regular items not discounted)
  // Takes priority over travel discount (matches backend logic)

  /**
   * Get gift box discount info from current cart
   * Returns { active, amount, name, subtotal }
   */
  function ojGetGiftBoxDiscount(cart) {
    try {
      if (!cart || !cart.items || cart.items.length === 0) {
        return { active: false, amount: 0, name: '', subtotal: 0 };
      }

      // Find gift box items via properties._gift_box === 'true'
      var giftBoxItems = cart.items.filter(function (item) {
        return item.properties && item.properties._gift_box === 'true';
      });

      if (giftBoxItems.length === 0) {
        return { active: false, amount: 0, name: '', subtotal: 0 };
      }

      // Calculate gift box subtotal in current currency
      var giftBoxSubtotal = 0;
      giftBoxItems.forEach(function (item) {
        var handle = item.handle;
        var priceInCurrency;

        // Priority 1: Use OjarPriceData (correct multi-currency price)
        if (window.OjarPriceData && window.OjarPriceData[handle] &&
            window.OjarPriceData[handle][currentCurrency] !== undefined) {
          priceInCurrency = window.OjarPriceData[handle][currentCurrency];
        } else {
          // Priority 2: Fallback to USD conversion via rate
          var conversionRate = getConversionRate(currentCurrency);
          priceInCurrency = (item.final_price / 100) * conversionRate;
        }

        giftBoxSubtotal += priceInCurrency * item.quantity;
      });

      // Get gift box name from first item's properties
      var giftBoxName = giftBoxItems[0].properties._gift_box_name || 'Gift Box';

      // Calculate 20% discount
      var discountAmount = giftBoxSubtotal * 0.20;

      return {
        active: true,
        amount: discountAmount,
        name: giftBoxName,
        subtotal: giftBoxSubtotal
      };
    } catch (e) {
      console.warn('[OjarCurrency] Gift box discount calc failed:', e);
      return { active: false, amount: 0, name: '', subtotal: 0 };
    }
  }

  /**
   * Render gift box discount line in cart drawer/page
   */
  function ojRenderGiftBoxLine(totalEl, info) {
    try {
      var container =
        totalEl.closest("cart-drawer") ||
        totalEl.closest(".drawer__footer") ||
        totalEl.closest("#main-cart-footer") ||
        totalEl.closest(".cart__footer") ||
        totalEl.parentElement;
      if (!container) return;

      // Remove existing gift box line (if any)
      var existing = container.querySelector(".oj-gift-box-discount-line");
      if (existing) existing.parentNode.removeChild(existing);

      // Don't render if discount not active
      if (!info || !info.active || info.amount <= 0) return;

      // Create discount line
      var line = document.createElement("div");
      line.className = "oj-gift-box-discount-line";
      line.setAttribute("style",
        "display:flex;justify-content:space-between;align-items:center;" +
        "margin:6px 0;font-size:14px;color:#c89e62;font-weight:600;");
      line.innerHTML =
        '<span>' + escapeHtml(info.name) + ' - 20% OFF</span>' +
        '<span>-' + formatPrice(info.amount, currentCurrency) + '</span>';

      // Insert before totals row
      var totalRow = totalEl.closest(".totals") || totalEl.parentElement;
      if (totalRow && totalRow.parentNode) {
        totalRow.parentNode.insertBefore(line, totalRow);
      }
    } catch (e) {
      console.warn('[OjarCurrency] Gift box line render failed:', e);
    }
  }

  /**
   * Safely escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (text === null || text === undefined) return '';
    var div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  // Debounce mechanism to prevent excessive applyMultiCurrencyPrices calls
  let priceApplyTimeout = null;
  let lastPriceApplyTime = 0;
  const MIN_PRICE_APPLY_INTERVAL = 50; // Minimum ms between price application calls

  /**
   * Cookie helper functions
   */
  function setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  function getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let c = cookies[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return null;
  }

  /**
   * Format price according to currency decimal and symbol configuration
   */
  function formatPrice(amount, currencyCode) {
    const config = CURRENCY_SYMBOLS[currencyCode] || CURRENCY_SYMBOLS["USD"];
    let formatted;

    if (config.decimals === 0) {
      formatted = Math.round(amount).toString();
    } else {
      formatted = parseFloat(amount).toFixed(config.decimals);
    }

    return config.symbol + formatted;
  }

  /**
   * Calculate conversion rate from USD to target currency using product prices
   */
  function getConversionRate(targetCurrency) {
    if (targetCurrency === "USD") return 1;

    if (!window.OjarPriceData) return 1;

    // Find first product that has prices in both USD and target currency
    for (const productHandle in window.OjarPriceData) {
      if (productHandle === "_conversion_rates") continue;

      const priceData = window.OjarPriceData[productHandle];
      if (priceData && priceData.USD && priceData[targetCurrency]) {
        const rate = priceData[targetCurrency] / priceData.USD;
        return rate;
      }
    }

    return 1; // Fallback: no conversion
  }

  /**
   * Calculate cart total from line items with custom prices.
   */
  function calculateCartTotalFromLineItems(scope) {
    const root = scope || document;
    const cartItems = root.querySelectorAll("tr[data-product-handle]");

    let cartTotal = 0;
    let itemsProcessed = 0;

    cartItems.forEach((item) => {
      const handle = item.dataset.productHandle;
      let isSample =
        item.dataset?.tags?.includes("sample-product") ||
        item.dataset?.isSample === "true" ||
        item.classList?.contains("cart-item__sample");
      let quantityInput =
        item.querySelector("[data-quantity-variant-id]") ||
        item.querySelector("input[type='number'][readonly]") ||
        item.querySelector("input[type='number']");

      if (
        !handle ||
        !quantityInput ||
        !window.OjarPriceData ||
        !window.OjarPriceData[handle]
      ) {
        return;
      }

      const quantity = parseInt(quantityInput.value) || 0;
      if (quantity <= 0) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];
      if (priceInCurrency === undefined) {
        return;
      }

      const lineTotal = priceInCurrency * quantity;
      cartTotal += isSample ? 0 : lineTotal;
      itemsProcessed++;
    });

    return itemsProcessed > 0 ? cartTotal : null;
  }

  /**
   * Apply custom prices from window.OjarPriceData to price elements (debounced)
   */
  function applyMultiCurrencyPrices() {
    clearTimeout(priceApplyTimeout);
    const now = Date.now();
    const timeSinceLastCall = now - lastPriceApplyTime;

    if (timeSinceLastCall >= MIN_PRICE_APPLY_INTERVAL) {
      applyMultiCurrencyPricesImpl();
      lastPriceApplyTime = now;
    } else {
      priceApplyTimeout = setTimeout(() => {
        applyMultiCurrencyPricesImpl();
        lastPriceApplyTime = Date.now();
      }, MIN_PRICE_APPLY_INTERVAL - timeSinceLastCall);
    }
  }

  let _priceApplyRunning = false;
  function applyMultiCurrencyPricesImpl() {
    if (_priceApplyRunning) return;
    _priceApplyRunning = true;
    try {
      _applyMultiCurrencyPricesCore();
    } finally {
      _priceApplyRunning = false;
    }
  }

  function _applyMultiCurrencyPricesCore() {
    if (!window.OjarPriceData || typeof window.OjarPriceData !== "object") {
      return;
    }

    // Find all price containers marked for Ojar formatting
    const priceContainers = document.querySelectorAll(
      '[data-ojar-price-formatted="true"]'
    );

    priceContainers.forEach((container) => {
      const handle = container.dataset.productHandle;

      if (container.dataset.priceVaries === "true") {
        return;
      }

      if (!handle || !window.OjarPriceData[handle]) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];

      if (priceInCurrency === undefined) {
        return;
      }

      const formattedPrice = formatPrice(priceInCurrency, currentCurrency);

      const priceItems = container.querySelectorAll(
        ".price-item--regular, .price-item--sale"
      );
      priceItems.forEach((item) => {
        if (item.closest("s")) {
          const originalText = item.getAttribute("data-ojar-original-price") || item.textContent;
          if (!item.hasAttribute("data-ojar-original-price")) {
            item.setAttribute("data-ojar-original-price", item.textContent);
          }
          const numericMatch = originalText.match(/[\d,]+\.?\d*/);
          if (numericMatch) {
            const usdValue = parseFloat(numericMatch[0].replace(/,/g, ""));
            const conversionRate = getConversionRate(currentCurrency);
            const convertedPrice = usdValue * conversionRate;
            item.textContent = formatPrice(convertedPrice, currentCurrency);
          }
          return;
        }
        item.textContent = formattedPrice;
      });
    });

    // Also update cart line item prices if they exist
    const cartLineItems = document.querySelectorAll("[data-product-handle]");
    cartLineItems.forEach((item) => {
      const handle = item.dataset.productHandle;

      if (!handle || !window.OjarPriceData[handle]) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];

      if (priceInCurrency === undefined) {
        return;
      }

      const formattedPrice = formatPrice(priceInCurrency, currentCurrency);

      if (item.classList.contains("oj-line-item__price")) {
        item.textContent = formattedPrice;
      }
    });

    // Update mobile cart item line total prices
    const cartMobileLineTotals = document.querySelectorAll(
      ".oj-cart-item-line-total-price"
    );
    cartMobileLineTotals.forEach((element) => {
      const cartRow = element.closest("tr[data-product-handle]");
      if (!cartRow) return;

      const handle = cartRow.dataset.productHandle;
      if (!handle || !window.OjarPriceData[handle]) {
        return;
      }

      const priceInCurrency = window.OjarPriceData[handle][currentCurrency];
      if (priceInCurrency === undefined) {
        return;
      }

      const quantityInput = cartRow.querySelector("[data-quantity-variant-id]");
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

      const lineTotal = priceInCurrency * quantity;
      const formattedLineTotal = formatPrice(lineTotal, currentCurrency);
      element.textContent = formattedLineTotal;
    });

    // Update cart total if it exists (update both cart page and drawer)
    const cartTotalValueElements = document.querySelectorAll(
      ".totals__total-value"
    );

    cartTotalValueElements.forEach((cartTotalValueElement) => {
      const scope =
        cartTotalValueElement.closest("cart-drawer") ||
        cartTotalValueElement.closest("#main-cart-footer")?.parentElement?.querySelector("cart-items#cart_page") ||
        document.querySelector("cart-items#cart_page") ||
        document;

      const cartTotal = calculateCartTotalFromLineItems(scope);
      if (cartTotal !== null) {
        // ===== DISCOUNT PRIORITY: Gift Box > Travel (matches backend) =====
        // Fetch cart data to check for gift box items
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            // RE-QUERY element in case DOM was re-rendered (e.g., by FreeSamples)
            var freshTotalEl = cartTotalValueElement;
            if (!document.body.contains(cartTotalValueElement)) {
              // Element disconnected, find fresh one
              var drawer = document.querySelector('cart-drawer');
              if (drawer) {
                freshTotalEl = drawer.querySelector('.totals__total-value');
              }
              if (!freshTotalEl) return;
            }

            var giftBoxInfo = ojGetGiftBoxDiscount(cart);
            var travelInfo = { pct: 0, amount: 0 };

            // Only compute travel discount if NO gift box (matches backend logic)
            if (!giftBoxInfo.active) {
              travelInfo = ojComputeTravelDiscount(scope);
            }

            var finalTotal = cartTotal;

            // Apply gift box discount (priority 1)
            if (giftBoxInfo.active && giftBoxInfo.amount > 0) {
              finalTotal = cartTotal - giftBoxInfo.amount;
              if (finalTotal < 0) finalTotal = 0;
            }
            // Otherwise apply travel discount (priority 2)
            else if (travelInfo && travelInfo.pct > 0 && travelInfo.amount > 0) {
              finalTotal = cartTotal - travelInfo.amount;
              if (finalTotal < 0) finalTotal = 0;
            }

            // Render appropriate discount line (only one active at a time)
            ojRenderGiftBoxLine(freshTotalEl, giftBoxInfo);
            // Hide travel line if gift box is active
            ojRenderTravelLine(
              freshTotalEl,
              giftBoxInfo.active ? { pct: 0, amount: 0 } : travelInfo
            );

            const formattedTotal = formatPrice(finalTotal, currentCurrency);
            freshTotalEl.textContent = formattedTotal;
            freshTotalEl.setAttribute("data-ojar-converted", "true");
            // Stamp with expected final total for defensive watcher
            freshTotalEl.setAttribute("data-ojar-expected-total", String(finalTotal));
          })
          .catch(function () {
            // Fallback: existing travel-only logic if cart fetch fails
            var travelInfo = ojComputeTravelDiscount(scope);
            var finalTotal = cartTotal;
            if (travelInfo && travelInfo.pct > 0 && travelInfo.amount > 0) {
              finalTotal = cartTotal - travelInfo.amount;
              if (finalTotal < 0) finalTotal = 0;
            }
            ojRenderTravelLine(cartTotalValueElement, travelInfo);
            const formattedTotal = formatPrice(finalTotal, currentCurrency);
            cartTotalValueElement.textContent = formattedTotal;
            cartTotalValueElement.setAttribute("data-ojar-converted", "true");
          });
      } else {
        // Fallback: Use Shopify AJAX Cart API to get the real USD total
        fetchCartTotalFromAPI(cartTotalValueElement);
      }
    });
  }

  /**
   * Fetch cart total from Shopify AJAX API (bulletproof fallback)
   */
  let _cartApiFetchInProgress = false;
  function fetchCartTotalFromAPI(element) {
    if (_cartApiFetchInProgress) return;
    _cartApiFetchInProgress = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const usdTotal = cart.total_price / 100;

        let convertedTotal = 0;

        cart.items.forEach(item => {
          const handle = item.handle;
          const quantity = item.quantity;
          const isSample = item.product_type === 'sample' ||
                          (item.tags && item.tags.includes('sample-product'));

          if (isSample || item.final_price === 0) {
            return;
          }

          if (window.OjarPriceData && window.OjarPriceData[handle] &&
              window.OjarPriceData[handle][currentCurrency] !== undefined) {
            convertedTotal += window.OjarPriceData[handle][currentCurrency] * quantity;
          } else {
            const conversionRate = getConversionRate(currentCurrency);
            convertedTotal += (item.final_price / 100) * conversionRate * quantity;
          }
        });

        // ===== DISCOUNT PRIORITY: Gift Box > Travel =====
        var giftBoxInfo = ojGetGiftBoxDiscount(cart);
        var finalTotal = convertedTotal;

        if (giftBoxInfo.active && giftBoxInfo.amount > 0) {
          finalTotal = convertedTotal - giftBoxInfo.amount;
          if (finalTotal < 0) finalTotal = 0;
        }

        ojRenderGiftBoxLine(element, giftBoxInfo);

        const formattedTotal = formatPrice(finalTotal, currentCurrency);
        element.textContent = formattedTotal;
        element.setAttribute("data-ojar-converted", "true");
        element.setAttribute("data-ojar-expected-total", String(finalTotal));
      })
      .catch(err => {
        console.warn("[OjarCurrency] Cart API fetch failed:", err);
      })
      .finally(() => {
        _cartApiFetchInProgress = false;
      });
  }

  /**
   * DEFENSIVE WATCHER (v2.2):
   * Self-healing mechanism for cart drawer race conditions.
   * 
   * Problem: Other scripts (e.g., FreeSamples in oj-compliementary.js) re-render
   * the cart drawer DOM, which wipes out our gift box discount line and resets
   * the total back to base price.
   * 
   * Solution: Periodically check if our discount line and expected total are
   * still in sync with cart data. If not, re-apply.
   * 
   * Performance: Only fetches cart.js when state mismatch detected.
   * Negligible CPU impact (<1% on idle, ~50ms work every 800ms).
   */
  let _watcherRunning = false;
  function setupDefensiveWatcher() {
    setInterval(function () {
      // Skip if already re-applying (prevent recursion)
      if (_watcherRunning) return;

      try {
        const drawer = document.querySelector('cart-drawer');
        const totalEl = drawer ? drawer.querySelector('.totals__total-value') : null;
        if (!drawer || !totalEl) return;

        // Quick check: does drawer have any products?
        const hasItems = drawer.querySelector('[data-product-handle]');
        if (!hasItems) return;

        const giftBoxLine = drawer.querySelector('.oj-gift-box-discount-line');
        const expectedTotal = totalEl.getAttribute('data-ojar-expected-total');
        const currentTotalText = totalEl.textContent || '';

        // Heuristic: if we have an expected total stored, check if current
        // displayed total matches. If mismatched by more than rounding error,
        // assume external script overrode our value.
        let needsReapply = false;

        if (expectedTotal !== null && expectedTotal !== '') {
          const expected = parseFloat(expectedTotal);
          // Extract numeric value from displayed text
          const displayedMatch = currentTotalText.match(/[\d,]+\.?\d*/);
          if (displayedMatch) {
            const displayed = parseFloat(displayedMatch[0].replace(/,/g, ''));
            // Allow 0.01 tolerance for rounding
            if (Math.abs(displayed - expected) > 0.01) {
              needsReapply = true;
            }
          }
        }

        // Also re-apply if we have items but no gift box line (might be needed)
        if (!needsReapply) {
          _watcherRunning = true;
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              const hasGiftBox = cart.items && cart.items.some(function (i) {
                return i.properties && i.properties._gift_box === 'true';
              });
              // If has gift box but no line displayed, re-apply
              if (hasGiftBox && !giftBoxLine) {
                applyMultiCurrencyPrices();
              }
              _watcherRunning = false;
            })
            .catch(function () {
              _watcherRunning = false;
            });
          return;
        }

        // Re-apply detected mismatch
        _watcherRunning = true;
        applyMultiCurrencyPrices();
        setTimeout(function () {
          _watcherRunning = false;
        }, 300);
      } catch (e) {
        _watcherRunning = false;
      }
    }, 800); // Check every 800ms — safe interval, minimal performance impact
  }

  function init() {
    // Priority 1: Check for user-selected currency in localStorage
    const userSelected = localStorage.getItem("ojar_user_selected") === "true";
    const savedCurrency = localStorage.getItem(CONFIG.storageKey);

    if (
      userSelected &&
      savedCurrency &&
      SUPPORTED_CURRENCIES.includes(savedCurrency)
    ) {
      currentCurrency = savedCurrency;
      currentCountry =
        localStorage.getItem(CONFIG.countryKey) || CONFIG.defaultCountry;

      setCookie(CONFIG.cookieName, currentCurrency, CONFIG.cookieDays);
      setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);
    } else {
      const cookieCurrency = getCookie(CONFIG.cookieName);

      if (cookieCurrency && SUPPORTED_CURRENCIES.includes(cookieCurrency)) {
        currentCurrency = cookieCurrency;
        currentCountry =
          getCookie(CONFIG.cookieCountryName) || CONFIG.defaultCountry;

        localStorage.setItem(CONFIG.storageKey, currentCurrency);
        localStorage.setItem(CONFIG.countryKey, currentCountry);
      } else {
        detectLocation();
        return;
      }
    }

    // Setup currency switcher UI
    setupCurrencySwitcher();

    // Set up instant conversion event listeners
    setupInstantConversionEvents();

    // Apply custom prices from OjarPriceData if available
    applyMultiCurrencyPrices();
    ojBuildTravelMap();

    // Subscribe to cart update events to recalculate all prices and totals
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        document.querySelectorAll("[data-ojar-converted]").forEach((el) => {
          el.removeAttribute("data-ojar-converted");
        });
        document.querySelectorAll("[data-ojar-original-usd-total]").forEach((el) => {
          el.removeAttribute("data-ojar-original-usd-total");
        });
        setTimeout(() => {
          ojBuildTravelMap();
          applyMultiCurrencyPrices();
        }, 300);
      });
    }

    // DEFENSIVE: Setup self-healing watcher for race conditions
    setupDefensiveWatcher();
  }

  /**
   * Detect user location via IP and set currency
   */
  async function detectLocation() {
    // ===== PRIMARY: Shopify's own country (from Markets, injected in theme.liquid) =====
    // Reliable, instant, no rate limit. Falls back to ipapi only if unavailable.
    try {
      var shopifyCountry = (typeof window.OJAR_SHOPIFY_COUNTRY === "string")
        ? window.OJAR_SHOPIFY_COUNTRY.trim().toUpperCase()
        : "";

      if (shopifyCountry && COUNTRY_CURRENCY_MAP[shopifyCountry]) {
        currentCountry = shopifyCountry;
        currentCurrency = COUNTRY_CURRENCY_MAP[shopifyCountry];

        localStorage.setItem(CONFIG.storageKey, currentCurrency);
        localStorage.setItem(CONFIG.countryKey, currentCountry);
        setCookie(CONFIG.cookieName, currentCurrency, CONFIG.cookieDays);
        setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);

        if (window.Shopify && window.Shopify.designMode) {
          // Design mode - prevent reload
        } else {
          window.location.reload();
        }
        return;
      }
    } catch (shopifyErr) {
      // Ignore and fall through to ipapi
    }

    // ===== FALLBACK: ipapi geolocation (original behaviour, unchanged) =====
    try {
      const response = await fetch(CONFIG.geoApiUrl);
      const data = await response.json();

      if (data.country_code) {
        currentCountry = data.country_code;
        currentCurrency =
          COUNTRY_CURRENCY_MAP[currentCountry] || CONFIG.defaultCurrency;
      } else {
        currentCurrency = CONFIG.defaultCurrency;
        currentCountry = CONFIG.defaultCountry;
      }
    } catch (error) {
      currentCurrency = CONFIG.defaultCurrency;
      currentCountry = CONFIG.defaultCountry;
    }

    localStorage.setItem(CONFIG.storageKey, currentCurrency);
    localStorage.setItem(CONFIG.countryKey, currentCountry);
    setCookie(CONFIG.cookieName, currentCurrency, CONFIG.cookieDays);
    setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);

    if (window.Shopify && window.Shopify.designMode) {
      // Design mode - prevent reload
    } else {
      window.location.reload();
    }
  }

  /**
   * Set up currency switcher dropdown UI
   */
  function setupCurrencySwitcher() {
    const switchers = document.querySelectorAll(".ojar-currency-switcher");

    switchers.forEach((container) => {
      const btn = container.querySelector(".ojar-currency-btn");
      const list = container.querySelector(".ojar-currency-list");
      const options = container.querySelectorAll(".ojar-currency-option");

      if (!btn || !list) return;

      const codeEl = container.querySelector("[data-current-currency]");
      if (codeEl) codeEl.textContent = currentCurrency;

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = btn.getAttribute("aria-expanded") === "true";

        document.querySelectorAll(".ojar-currency-btn").forEach((b) => {
          if (b !== btn) {
            b.setAttribute("aria-expanded", "false");
          }
        });
        document.querySelectorAll(".ojar-currency-list").forEach((l) => {
          if (l !== list) {
            l.setAttribute("aria-hidden", "true");
            l.hidden = true;
          }
        });

        if (!isExpanded) {
          btn.setAttribute("aria-expanded", "true");
          list.setAttribute("aria-hidden", "false");
          list.hidden = false;
        } else {
          btn.setAttribute("aria-expanded", "false");
          list.setAttribute("aria-hidden", "true");
          list.hidden = true;
        }
      });

      options.forEach((opt) => {
        opt.addEventListener("click", (e) => {
          e.stopPropagation();

          const code = opt.dataset.currencyCode;
          if (code && SUPPORTED_CURRENCIES.includes(code)) {
            setCurrency(code);
          }
        });
      });
    });

    document.addEventListener("click", (e) => {
      if (!e.target.closest(".ojar-currency-switcher")) {
        document.querySelectorAll(".ojar-currency-btn").forEach((b) => {
          b.setAttribute("aria-expanded", "false");
        });
        document.querySelectorAll(".ojar-currency-list").forEach((l) => {
          l.setAttribute("aria-hidden", "true");
          l.hidden = true;
        });
      }
    });

    document
      .querySelectorAll("[data-ojar-currency-select]")
      .forEach((select) => {
        select.value = currentCurrency;

        select.addEventListener("change", (e) => {
          const code = e.target.value;
          if (code && SUPPORTED_CURRENCIES.includes(code)) {
            setCurrency(code);
          }
        });
      });
  }

  /**
   * Setup instant currency conversion event handlers
   */
  function setupInstantConversionEvents() {
    if (window.OjarCurrencyEventsSetup === true) {
      return;
    }
    window.OjarCurrencyEventsSetup = true;

    // 1. Quantity changes on cart page
    document.addEventListener(
      "change",
      (e) => {
        if (
          e.target.matches('[data-quantity-input], input[name*="quantity"]')
        ) {
          setTimeout(() => applyMultiCurrencyPrices(), 0);
        }
      },
      true
    );

    // 2. Cart drawer open/close events
    document.addEventListener(
      "click",
      (e) => {
        if (
          e.target.closest(
            '[data-cart-drawer-trigger], [href*="/cart"], .cart-icon-bubble, #cart-icon-bubble'
          )
        ) {
          setTimeout(() => { ojBuildTravelMap(); applyMultiCurrencyPrices(); }, 150);
        }
      },
      true
    );

    // 3. Variant selection
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.matches('[data-single-variant-selector], [name="id"]')) {
          setTimeout(() => applyMultiCurrencyPrices(), 100);
        }
      },
      true
    );

    // 4. Form submissions (add to cart, etc.)
    document.addEventListener(
      "submit",
      (e) => {
        if (e.target.matches('[action*="/cart"], form[data-add-to-cart]')) {
          setTimeout(() => applyMultiCurrencyPrices(), 200);
        }
      },
      true
    );

    // 5. Intercept fetch calls for cart operations (only wrap once)
    if (!window.OjarCurrencyFetchWrapped) {
      window.OjarCurrencyFetchWrapped = true;
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const result = originalFetch.apply(this, args);
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const isCartRead = url === '/cart.js' || url === '/cart.json';
        if (!isCartRead) {
          result
            .then(() => {
              setTimeout(() => applyMultiCurrencyPrices(), 200);
            })
            .catch(() => { });
        }
        return result;
      };
    }
  }

  /**
   * Set currency (user selection)
   */
  function setCurrency(currencyCode) {
    if (!SUPPORTED_CURRENCIES.includes(currencyCode)) return;

    currentCurrency = currencyCode;

    localStorage.setItem(CONFIG.storageKey, currencyCode);
    localStorage.setItem("ojar_user_selected", "true");

    setCookie(CONFIG.cookieName, currencyCode, CONFIG.cookieDays);
    setCookie(CONFIG.cookieCountryName, currentCountry, CONFIG.cookieDays);

    document.querySelectorAll("[data-ojar-original-price]").forEach((el) => {
      // Keep originals
    });
    document.querySelectorAll("[data-ojar-converted]").forEach((el) => {
      el.removeAttribute("data-ojar-converted");
    });

    applyMultiCurrencyPrices();

    const currencySwitchers = document.querySelectorAll(
      "[data-current-currency]"
    );
    currencySwitchers.forEach((el) => {
      el.textContent = currencyCode;
    });

    document
      .querySelectorAll("[data-ojar-currency-select]")
      .forEach((select) => {
        select.value = currencyCode;
      });
  }

  /**
   * Public API
   */
  window.OjarCurrency = {
    init,
    setCurrency,
    getCurrentCurrency: () => currentCurrency,
    getCurrencyMetafieldKey: () =>
      currentCurrency === "USD"
        ? ""
        : `product_price_in_${currentCurrency.toLowerCase()}`,
    getCurrentCountry: () => currentCountry,
    getSupportedCurrencies: () => SUPPORTED_CURRENCIES,
    applyMultiCurrencyPrices,
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();